<script setup lang="ts">
import { ref, computed } from 'vue'

import { formatNumber } from '../../utils/numbers'

export type RatingSize = 'small' | 'medium' | 'large' | 'responsive'

const starRanges = {
  percent25: {
    min: 0.15,
    max: 0.34
  },
  percent50: {
    min: 0.35,
    max: 0.64
  },
  percent75: {
    min: 0.65,
    max: 0.84
  }
}

const props = withDefaults(defineProps<{
  average?: number,
  editable?: boolean,
  averageFractionDigits?: number,
  averagePostfix?: string,
  showAverage?: boolean,
  quantity?: string,
  size?: RatingSize,
  isRecipeRating?: boolean
}>(), {
  average: 0,
  editable: false,
  averageFractionDigits: 0,
  averagePostfix: '',
  showAverage: false,
  quantity: undefined,
  size: 'large',
  isRecipeRating: false
})

const emit = defineEmits<{
  (e: 'update:average', pos: number): void
}>()

const hoveredPos = ref<number | null>(null)

const averageString = computed(() => formatNumber(props.average, props.averageFractionDigits))
const setTabindex = computed(() => props.editable ? '0' : undefined)

const getStarClasses = (pos: number) => {
  const value = props.editable && hoveredPos.value !== null ? hoveredPos.value : props.average
  const isValueBetweenPosAndNext = value > pos && value < pos + 1
  const leftover = isValueBetweenPosAndNext ? ((value * 10) - (pos * 10)) / 10 : 0

  return {
    'icon-star--25': leftover >= starRanges.percent25.min && leftover < starRanges.percent50.min,
    'icon-star--50': leftover >= starRanges.percent50.min && leftover < starRanges.percent75.min,
    'icon-star--75': leftover >= starRanges.percent75.min && leftover <= starRanges.percent75.max,
    'icon-star--100': value >= pos + 1 || leftover > starRanges.percent75.max
  }
}

const hover = (pos: number | null) => {
  if (props.editable) {
    hoveredPos.value = pos
  }
}
</script>

<template>
  <div :class="[
    'bx-rating',
    `bx-rating--${size}`,
    'bx-typo--rating',
    { ['bx-rating--editable']: editable }]">
    <span class="bx-rating__stars"
          data-tc="rating-stars">
      <i v-for="pos in 5"
         :key="pos"
         class="icon icon-star"
         :class="getStarClasses(pos - 1)"
         :tabindex="setTabindex"
         @mouseover="hover(pos)"
         @mouseleave="hover(null)"
         @click="editable && emit('update:average', pos)"
         @keyup.enter="editable && emit('update:average', pos)">
        <svg><use href="#star" /></svg>
      </i>
    </span>
    <div>
      <span v-if="showAverage"
            class="bx-rating__average"
            data-tc="rating-average">
        <span :class="isRecipeRating ? 'bx-rating__average--bold' : ''">
          {{ averageString }}
        </span>{{ averagePostfix }}
      </span>
      <span v-if="quantity"
            class="bx-rating__quantity">
        {{ quantity }}
      </span>
    </div>
  </div>
</template>
